// ChannelStream — a channel is a PLACE (spec §1 / §5 rule 8).
//
// Persistent, scrollable, never resolved. The composer is pinned at the bottom;
// new messages append below. No resolve or assign control — fn_thread_resolve /
// fn_thread_assign refuse on a channel ("channels and DMs are not resolvable"),
// and a disabled button that raises when clicked is worse than none. The channel
// header shows the #name + client-facing left border + label. Paginates backwards
// with p_before (never fetch the 10,447-message channel whole).
import React, { useCallback, useEffect, useState, useRef } from "react";
import { readConversation } from "../../../api/conversations";
import { Composer } from "../kit";
import { EmptyState, ErrorState } from "../../shared/StateMessage";
import PanelErrorBoundary from "../../shared/PanelErrorBoundary";
import MessageRow from "./MessageRow";
import { fmtNumber } from "../../shared/format";
import { useScrollBottom } from "./useScrollBottom";
import { useInfiniteScrollTop } from "./useInfiniteScroll";
import { prependOlder, pageExhausted } from "./mergeMessages";

const PAGE_SIZE = 60;
const CLIENT_BORDER = "border-l-4 border-l-[#32B1FF]";

/**
 * @param {object} props
 * @param {object} props.channel   { id, name, client_facing, client_id, messages, ... } from the rail's rooms data
 */
export default function ChannelStream({ channel }) {
  const ch = channel || {};
  const [status, setStatus] = useState("loading");
  const [messages, setMessages] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const loadingMoreRef = useRef(false);

  // Pin the messages scroll container to the bottom on open and after a send.
  const { ref: scrollRef, scrollToBottomAfterPaint } = useScrollBottom();

  const fetchChannel = useCallback(async () => {
    const res = await readConversation({ type: "channel", id: ch.id, limit: PAGE_SIZE });
    const data = res || {};
    setMessages(Array.isArray(data.messages) ? data.messages : []);
    setNextBefore(data.next_before || null);
    setTotalCount(typeof data.channel?.messages === "number" ? data.channel.messages : null);
    setExhausted(false);
    return data;
  }, [ch.id]);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      await fetchChannel();
      setStatus("ready");
    } catch (err) {
      setStatus("error");
    }
  }, [fetchChannel]);

  useEffect(() => { load(); }, [load]);

  // Opening a channel (or switching channels) jumps to the newest message.
  useEffect(() => {
    if (status !== "ready") return;
    scrollToBottomAfterPaint();
  }, [status, ch.id, scrollToBottomAfterPaint]);

  // After a send: re-fetch (no loading skeleton — keeps the reader's place) and
  // jump to the just-sent message. Silent on error so a failed refresh never
  // wipes the list the user is already looking at.
  const refresh = useCallback(async () => {
    try { await fetchChannel(); } catch { /* silent */ }
    scrollToBottomAfterPaint();
  }, [fetchChannel, scrollToBottomAfterPaint]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    // Cursor: prefer next_before; otherwise the oldest loaded message's `at`.
    const cursor = nextBefore || messages[0]?.at;
    if (!cursor) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const res = await readConversation({ type: "channel", id: ch.id, limit: PAGE_SIZE, before: cursor });
      const data = res || {};
      const older = Array.isArray(data.messages) ? data.messages : [];
      setMessages((prev) => prependOlder(prev, older));
      setNextBefore(data.next_before || null);
      if (pageExhausted(older, cursor)) setExhausted(true);
    } catch (err) { /* silent — first page already loaded */ }
    finally { loadingMoreRef.current = false; setLoadingMore(false); }
  }, [ch.id, nextBefore, messages]);

  // hasMore: the backend does NOT return next_before — derive from total count,
  // and honor `exhausted` so an inclusive/empty page stops paging.
  const hasMore = !exhausted && ((typeof totalCount === "number" && totalCount > messages.length) || !!nextBefore);

  const { sentinelRef } = useInfiniteScrollTop({
    scrollRef,
    hasMore,
    loading: loadingMore,
    ready: status === "ready",
    onLoadMore: loadMore,
  });

  if (status === "loading") {
    return (
      <div className="space-y-2 p-4" role="status" aria-label="Loading">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="h-16 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
        ))}
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="p-4">
        <ErrorState variant="inset" message="This conversation is temporarily unavailable. Please try again shortly." onRetry={load} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" data-testid="channel-stream">
      <header
        className={`flex items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-800 ${ch.client_facing ? CLIENT_BORDER : "border-l-4 border-l-transparent"}`}
      >
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">#{ch.name || "channel"}</span>
        {ch.client_facing && (
          <span className="shrink-0 rounded bg-[#32B1FF]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#32B1FF]">
            Client
          </span>
        )}
        {typeof ch.messages === "number" && (
          <span className="ml-auto text-xs text-gray-400 tabular-nums">{fmtNumber(ch.messages)} messages</span>
        )}
      </header>

      <PanelErrorBoundary label="channel-stream-messages">
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-3" data-testid="channel-scroll">
          {hasMore && !loadingMore && (
            <div className="mb-3 flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-gray-800"
                data-testid="stream-load-more"
              >
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="18 15 12 9 6 15" />
                </svg>
                Load older messages
              </button>
            </div>
          )}
          {loadingMore && (
            <div className="mb-3 flex items-center justify-center gap-2" data-testid="stream-loading-more">
              <svg className="h-4 w-4 animate-spin text-[#32B1FF]" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-xs text-gray-500 dark:text-gray-400">Loading older messages…</span>
            </div>
          )}
          {messages.length === 0 ? (
            <EmptyState variant="inset" message="No messages yet." padding="py-8" />
          ) : (
            <div className="space-y-1.5">
              {messages.map((m) => (
                <MessageRow key={m.id} message={m} />
              ))}
            </div>
          )}
        </div>
      </PanelErrorBoundary>

      <div className="shrink-0 border-t border-gray-200 p-3 dark:border-gray-800">
        <Composer channel="chat" threadId={ch.id} clientId={ch.client_id} onSent={refresh} />
      </div>
    </div>
  );
}
